import type * as ents from '@attain-apps/entities'
import * as store from '@attain-apps/fe/web/main/lib/store'

import type * as ecModels from '../../../models'

import type { Plugin } from './plugin'
import type { State } from './state'

/**
 * Core actions for this {@link Plugin}
 */
// NOTE: intentionally missing return type so that type can be inferred
export function getActionsConfig<
  TTypeParams extends ecModels.TypeParams.Constraint,
>() {
  return {
    /**
     * Retrieves all attributes for the entity collection
     */
    getAttributes: store.actions.request({
      input: store.actions.Data.NONE,
      output: store.actions.Data.asIs<{
        readonly attributes: readonly ecModels.Attribute<TTypeParams>[]
      }>(),
      reducersDeprecated: {
        error: (state, { error }) => ({ ...state, error, isLoading: false }),
        request: state => ({ ...state, isLoading: true }),
        success: (state, { response: { attributes } }) => ({
          ...state,
          attributes,
          isLoading: false,
        }),
      },
    }),
    /**
     * Hides the given attribute
     */
    hideAttribute: store.actions.standalone({
      data: store.actions.Data.asIs<{
        attributeId: ents.core.common.Attribute.Id
      }>(),
      reducerDeprecated: (state, { attributeId }) => {
        state = { ...state }
        state.attributes = state.attributes?.map(attribute =>
          attribute.id === attributeId
            ? { ...attribute, isHidden: true }
            : attribute,
        )
        return state
      },
    }),
    /**
     * Reorders the given attributes
     */
    reorderAttributes: store.actions.standalone({
      data: store.actions.Data.asIs<{ fromIndex: number; toIndex: number }>(),
      reducerDeprecated: (state, { fromIndex, toIndex }) => {
        const { attributes } = state
        return {
          ...state,
          attributes: attributes?.map((attribute, i) =>
            (i < fromIndex && i < toIndex) || (i > fromIndex && i > toIndex)
              ? attribute
              : attributes[
                  i === toIndex ? fromIndex : i + (fromIndex < toIndex ? 1 : -1)
                ],
          ),
        }
      },
    }),
    /**
     * Shows the given attribute
     */
    showAttribute: store.actions.standalone({
      data: store.actions.Data.asIs<{
        attributeId: ents.core.common.Attribute.Id
      }>(),
      reducerDeprecated: (state, { attributeId }) => {
        state = { ...state }
        state.attributes = state.attributes?.map(attribute =>
          attribute.id === attributeId
            ? { ...attribute, isHidden: false }
            : attribute,
        )
        return state
      },
    }),
  } as const satisfies store.actions.Config.Constraint<State<TTypeParams>>
}
